import React, {useEffect} from "react";
import {
    IonAccordion,
    IonAccordionGroup,
    IonButton,
    IonCard, IonCardContent,
    IonCardHeader, IonCardTitle, IonCol,
    IonContent, IonGrid,
    IonIcon, IonInput, IonItem, IonLabel,
    IonLoading,
    IonPage,
    IonRow,
    IonSelect,
    IonSelectOption, IonTextarea, IonToggle, useIonAlert, useIonModal
} from "@ionic/react";
import {
    addOutline,
    addSharp, arrowBackOutline, arrowBackSharp, arrowUpOutline,
    attachOutline,
    attachSharp, cloudUploadOutline, cloudUploadSharp, paperPlaneOutline, paperPlaneSharp,
    pencilOutline,
    pencilSharp, trashOutline, trashSharp
} from "ionicons/icons";
import './AcaViewContact.css';
import {AcaHeader} from "./AcaHeader";
import {Attachment, ContactDependentType, ContactNote, ContactType, RecentOpportunityChanges} from "./types";
import {useMutation} from "@apollo/client";
import {
    ACA_ADD_DEPENDENT, ACA_DELETE_DEPENDENT,
    ACA_GET_CONTACT,
    ACA_GET_IMAGE_URL, ACA_ISSUE_IMAGE_REPLACEMENT,
    ACA_UPDATE_CONTACT, ACA_UPDATE_DEPENDENT,
    ACA_UPLOAD_IMAGE
} from "../../schemas/aca";
import {useHistory, useRouteMatch} from "react-router";
import {formatPhone, formatStatus} from "../../utils";
import useAuth from "../../hooks/useAuth";
import AcaUploadFile, {AcaSingleUploadI} from "./components/AcaUploadFile";
import ModalImage from "./modals/ModalImage";
import ModalImageReplacementRequest from "./modals/ModalImageReplacementRequest";
import {OverlayEventDetail} from "@ionic/react/dist/types/components/react-component-lib/interfaces";
import MissingItem from "../../components/MissingItem";


const AcaEditContact: React.FC<any> = () => {
    const {claims} = useAuth();
    const history = useHistory();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);
    const [showNotes, setShowNotes] = React.useState<boolean>(false);
    const [contact, setContact] = React.useState<ContactType | undefined>(undefined);
    const [contactNotes, setContactNotes] = React.useState<ContactNote[] | undefined>(undefined);
    const [opportunities, setOpportunities] = React.useState<RecentOpportunityChanges[] | undefined>(undefined);
    const [dependents, setDependents] = React.useState<ContactDependentType[] | undefined>(undefined);
    const [imageUrl, setImageUrl] = React.useState<string | undefined>(undefined);
    const [editDependent, setEditDependent] = React.useState<ContactDependentType | undefined>(undefined);
    const [addDependent, setAddDependent] = React.useState<ContactDependentType | undefined>(undefined);
    const [openOpportunities, setOpenOpportunities] = React.useState<boolean>(false);

    const [presentImage, dismissImage] = useIonModal(ModalImage, {
        imageUrl: imageUrl,
        dismiss: (data: string, role: string) => dismissImage(data, role)
    });
    const [presentImageReplacement, dismissImageReplacement] = useIonModal(ModalImageReplacementRequest, {
        contact: contact,
        replacementType: 'PHOTO',
        dismiss: (data: string, role: string) => dismissImageReplacement(data, role)
    });


    const [acaGetContactMutation] = useMutation(ACA_GET_CONTACT);
    const [acaUpdateContactMutation] = useMutation(ACA_UPDATE_CONTACT);
    const [uploadImageMutation] = useMutation(ACA_UPLOAD_IMAGE);
    const [getImageUrlMutation] = useMutation(ACA_GET_IMAGE_URL);
    const [addDependentMutation] = useMutation(ACA_ADD_DEPENDENT);
    const [updateDependentMutation] = useMutation(ACA_UPDATE_DEPENDENT);
    const [deleteDependentMutation] = useMutation(ACA_DELETE_DEPENDENT);
    const [issueImageReplacementMutation] = useMutation(ACA_ISSUE_IMAGE_REPLACEMENT);
    const [presentAlert, dismissAlert] = useIonAlert();
    const match:any = useRouteMatch();

    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    // replacing images
    const [changingPhoto1, setChangingPhoto1] = React.useState<boolean>(false);
    const [photo1Attachments, setPhoto1Attachments] = React.useState<Attachment[]>([]);
    const [changingPhoto2, setChangingPhoto2] = React.useState<boolean>(false);
    const [photo2Attachments, setPhoto2Attachments] = React.useState<Attachment[]>([]);

    const addPhoto1Attachment = async ({ filename, url, error }: AcaSingleUploadI) => {
        if (error) {
            return alert(error);
        }
        const fileParts = filename.split('.');
        const fileType = fileParts[fileParts.length-1];
        if (fileType.toUpperCase() !== 'PNG' && fileType.toUpperCase() !== 'JPEG' && fileType.toUpperCase() !== 'JPG') {
            return alert('Only able to upload PNG or JPG files');
        }
        setIsLoading(true);

        let newFileType : 'JPG' | 'PNG' = 'JPG';
        if (fileType.toUpperCase() === 'PNG') {
            newFileType = 'PNG';
        }

        if (contact && contact.contactId) {
            setPhoto1Attachments([{filename, url, label: ""}, ...photo1Attachments])
            const uploadResult = await uploadImageMutation({
                variables: {
                    imageUrl: url,
                    imageName: filename,
                    imageFileType: newFileType
                }
            });
            const updateContactResult = await acaUpdateContactMutation({
                variables: {
                    data: {
                        contactId: contact.contactId,
                        photo1Id: Number(uploadResult.data.acaUploadImage.imageId)
                    }
                }
            });
            setChangingPhoto1(false);
            await loadData(contact.contactId);
        }
    }
    const addPhoto2Attachment = async ({ filename, url, error }: AcaSingleUploadI) => {
        if (error) {
            return alert(error);
        }
        if (contact && contact.contactId) {
            const fileParts = filename.split('.');
            const fileType = fileParts[fileParts.length-1];
            if (fileType.toUpperCase() !== 'PNG' && fileType.toUpperCase() !== 'JPEG' && fileType.toUpperCase() !== 'JPG') {
                return alert('Only able to upload PNG or JPG files');
            }

            setIsLoading(true);

            let newFileType : 'JPG' | 'PNG' = 'JPG';
            if (fileType.toUpperCase() === 'PNG') {
                newFileType = 'PNG';
            }
            setPhoto2Attachments([{filename, url, label: ""}, ...photo2Attachments]);
            const uploadResult = await uploadImageMutation({
                variables: {
                    imageUrl: url,
                    imageName: filename,
                    imageFileType: newFileType
                }
            });
            const updateContactResult = await acaUpdateContactMutation({
                variables: {
                    data: {
                        contactId: contact.contactId,
                        photo2Id: Number(uploadResult.data.acaUploadImage.imageId)
                    }
                }
            });
            setChangingPhoto2(false);

            await loadData(contact.contactId);
        }
    }
    ////

    useEffect(() => {
        // check for roles
        let hasEdit = false;
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
            hasEdit = true;
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
            hasEdit = true;
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
            hasEdit = true;
        }

        if (!hasEdit) {
            alert("You do not have permission to this page!");
            history.goBack();
            return;
        }
        if (match.params.id && !isNaN(match.params.id)) {
            loadData(Number(match.params.id));
        }
    }, []);

    const promptImageReplace = async (photoNum: number) => {
        if (contact) {
            if (photoNum == 1) {
                setChangingPhoto1(false); //reset the changing option in case it was active
            } else if (photoNum == 2) {
                setChangingPhoto2(false); //reset the changing option in case it was active
            }
            presentImageReplacement({
                onWillDismiss: async (ev: CustomEvent<OverlayEventDetail>) => {
                    if (ev.detail.role === 'confirm') {
                        setIsLoading(true);
                        let contactMethod: 'EMAIL' | 'SMS' = 'EMAIL';
                        if (ev.detail.data === 'SMS') {
                            contactMethod = 'SMS';
                        }

                        let result = await issueImageReplacementMutation({
                            variables: {
                                data: {
                                    type: 'PHOTO' + photoNum,
                                    deliveryMethod: contactMethod,
                                    contactId: contact.contactId,
                                }
                            }
                        });
                        setIsLoading(false);
                        if (!result.data.acaIssueImageReplacement.success) {
                            return alert(result.data.acaIssueImageReplacement.error);
                        }

                        if (contactMethod === 'SMS') {
                            return await presentAlert('Request has been sent to ' + formatPhone(contact.phone));
                        } else {
                            return await presentAlert('Request has been sent to ' + contact.email);
                        }

                    }
                }
            });
        }
    };

    const updateContact = (field: string, value:any) => {
        if (contact) {
            if (!value) {
                value = null;
            }
            let tmpContact = contact;
            switch (field) {
                case 'firstName':
                    tmpContact.firstName = value;
                    break;
                case 'middleName':
                    tmpContact.middleName = value;
                    break;
                case 'lastName':
                    tmpContact.lastName = value;
                    break;
                case 'suffix':
                    tmpContact.suffix = value;
                    break;

                case 'email':
                    tmpContact.email = value;
                    break;
                case 'phone':
                    tmpContact.phone = value;
                    break;

                case 'dob':
                    tmpContact.dob = value;
                    break;
                case 'ssn':
                    tmpContact.ssn = value;
                    break;

                case 'addressLine1':
                    tmpContact.addressLine1 = value;
                    break;
                case 'addressLine2':
                    tmpContact.addressLine2 = value;
                    break;
                case 'addressCity':
                    tmpContact.addressCity = value;
                    break;
                case 'addressState':
                    tmpContact.addressState = value;
                    break;
                case 'addressZip':
                    tmpContact.addressZip = value;
                    break;

                case 'annualIncome':
                    if (value) {
                        tmpContact.annualIncome = Number(value);
                    } else {
                        tmpContact.annualIncome = 0;
                    }
                    break;
                case 'employmentType':
                    tmpContact.employmentType = value;
                    break;
                case 'employmentDescription':
                    tmpContact.employmentDescription = value;
                    break;
                case 'mainDoctor':
                    tmpContact.mainDoctor = value;
                    break;
                case 'medications':
                    tmpContact.medications = value;
                    break;
                case 'smoker':
                    tmpContact.smoker = value;
                    break;
                case 'photo1Id':
                    tmpContact.photo1Id = value;
                    break;
                case 'photo2Id':
                    tmpContact.photo2Id = value;
                    break;


                case 'status':
                    tmpContact.status = value;
                    break;

            }
            setContact(tmpContact);
        }
    }


    const loadData = async (id: number) => {
        setIsLoading(true);
        try {
            let contactResult = await acaGetContactMutation({variables: {contactId: id}});
            if (contactResult.data?.acaGetContact.success) {
                contactResult.data.acaGetContact.contact.dob = null; // clear out
                contactResult.data.acaGetContact.contact.ssn = null; // clear out
                setContact(contactResult.data.acaGetContact.contact);
                setContactNotes(contactResult.data.acaGetContact.notes);
                setOpportunities(contactResult.data.acaGetContact.opportunities);
                setDependents(contactResult.data.acaGetContact.dependents);
                setOpenOpportunities(false);
                contactResult.data.acaGetContact.opportunities.forEach((opp: {opportunityStatus: string}) => {
                    if (opp.opportunityStatus === 'OPEN') {
                        setOpenOpportunities(true);
                    }
                });
            }
        } catch (e: any) {
            console.error(e);
            alert(e.message);
        } finally {
            setIsLoading(false);
        }
    };
    const saveData = async() => {
        if (contact) {
            try {
                let contactUpdateResult =
                    await acaUpdateContactMutation({
                        variables: {
                            data: {
                                contactId: contact.contactId,
                                status: contact.status,
                                firstName: contact.firstName,
                                middleName: contact.middleName,
                                lastName: contact.lastName,
                                email: contact.email,
                                phone: contact.phone,
                                dob: contact.dob,
                                ssn: contact.ssn,
                                addressLine1: contact.addressLine1,
                                addressLine2: contact.addressLine2,
                                addressCity: contact.addressCity,
                                addressState: contact.addressState,
                                addressZip: contact.addressZip,
                                annualIncome: contact.annualIncome,
                                employmentType: contact.employmentType,
                                employmentDescription: contact.employmentDescription,
                                mainDoctor: contact.mainDoctor,
                                medications: contact.medications,
                                smoker: contact.smoker,
                                fusionCustomerId: contact.fusionCustomerId,
                                etc: contact.etc,
                                photo1Id: contact.photo1Id,
                                photo2Id: contact.photo2Id
                            }
                        }
                    });
                if (contactUpdateResult.data?.acaUpdateContact.success && contact.contactId) {
                    await presentAlert('Contact updated successfully');
                    await loadData(contact.contactId);
                }
                if (!contactUpdateResult.data.acaUpdateContact.success) {
                    await presentAlert(contactUpdateResult.data.acaUpdateContact.error)
                }
            } catch (e: any) {
                console.log(e);
                await presentAlert(e.message);
            }
        }
    };
    const expandImage = async (imageId: number | null) => {
        if (imageId) {
            const imageLookup = await getImageUrlMutation({variables: {imageId}});

            if (!imageLookup.data.acaGetImageUrl.success) {
                return alert('Unable to lookup image!');
            }

            setImageUrl(imageLookup.data.acaGetImageUrl.url);
            await presentImage();
        }

    };

    const saveEditDependent = async() => {
        if (contact && editDependent) {
            // make call to save dependent info

            setIsLoading(true);
            const result = await updateDependentMutation({variables: {data: {
                        dependentId: editDependent.id,
                        firstName: editDependent.firstName,
                        middleName: editDependent.middleName,
                        lastName: editDependent.lastName,
                        suffix: editDependent.suffix,
                        dob: editDependent.dob?.includes('*') ? null : editDependent.dob,
                        ssn: editDependent.ssn?.includes('*') || !editDependent.ssn ? null : editDependent.ssn
                    }}});
            if (!result.data.acaUpdateDependent.success) {
                setIsLoading(false);
                return alert(result.data.acaUpdateDependent.error);
            }

            setEditDependent(undefined);
            loadData(Number(match.params.id));
        }
    };
    const updateDependent = async (field: string, value: string | null | undefined) => {
        if (editDependent && value) {
            if (field === 'firstName') {
                editDependent.firstName = value;
                setEditDependent(editDependent);
            } else if (field === 'middleName') {
                editDependent.middleName = value;
                setEditDependent(editDependent);
            } else if (field === 'lastName') {
                editDependent.lastName = value;
                setEditDependent(editDependent);
            } else if (field === 'suffix') {
                editDependent.suffix = value;
                setEditDependent(editDependent);
            } else if (field === 'dob') {
                editDependent.dob = value;
                setEditDependent(editDependent);
            } else if (field === 'ssn') {
                editDependent.ssn = value;
                setEditDependent(editDependent);
            }
        }
    }
    const saveAddDependent = async() => {
        if (contact && addDependent) {
            // make call to save dependent info
            setIsLoading(true);
            const result = await addDependentMutation({variables: {data: {
                        contactId: addDependent.contactId,
                        firstName: addDependent.firstName,
                        middleName: addDependent.middleName,
                        lastName: addDependent.lastName,
                        suffix: addDependent.suffix,
                        dob: addDependent.dob,
                        ssn: addDependent.ssn
                    }}});
            if (!result.data.acaAddDependent.success) {
                setIsLoading(false);
                return alert(result.data.acaAddDependent.error);
            }
            setAddDependent(undefined);
            loadData(Number(match.params.id));
        }
    };
    const updateAddDependent = async (field: string, value: string | null | undefined) => {
        if (addDependent && value) {
            if (field === 'firstName') {
                addDependent.firstName = value;
                setAddDependent(addDependent);
            } else if (field === 'middleName') {
                addDependent.middleName = value;
                setAddDependent(addDependent);
            } else if (field === 'lastName') {
                addDependent.lastName = value;
                setAddDependent(addDependent);
            } else if (field === 'suffix') {
                addDependent.suffix = value;
                setAddDependent(addDependent);
            } else if (field === 'dob') {
                addDependent.dob = value;
                setAddDependent(addDependent);
            } else if (field === 'ssn') {
                addDependent.ssn = value;
                setAddDependent(addDependent);
            }
        }
    }

    const deleteDependent = async (depId: number | null) => {
        if (depId) {
            setIsLoading(true);
            const result = await deleteDependentMutation({
                variables: {
                    data: {
                        dependentId: depId
                    }
                }
            });
            if (!result.data.acaDeleteDependent.success) {
                setIsLoading(false);
                return alert(result.data.acaDeleteDependent.error);
            }
            loadData(Number(match.params.id));
        }
    };

    return (
        <IonPage>

            <AcaHeader pageTitle="ACA Edit Contact"></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-contact">
                <div style={{padding: '10px'}}>

                    {contact &&
                        <div>
                            <a href={'/aca/view-contact/' + contact.contactId}><IonIcon md={arrowBackOutline} ios={arrowBackSharp}></IonIcon> Go Back To Contact</a>
                            <p>Updating information for contact:</p>
                            <table style={{borderCollapse: 'separate', borderSpacing: '5px'}}>
                                <tbody>
                                <tr>
                                    <th>First Name:</th>
                                    <td>{contact.firstName}</td>
                                </tr>
                                <tr>
                                    <th>Middle Name:</th>
                                    <td>{contact.middleName}</td>
                                </tr>
                                <tr>
                                    <th>Last Name:</th>
                                    <td>{contact.lastName}</td>
                                </tr>
                                <tr>
                                    <th>Suffix:</th>
                                    <td>{contact.suffix}</td>
                                </tr>
                                <tr>
                                    <th>Status:</th>
                                    <td>{formatStatus(contact.status)}</td>
                                </tr>
                                <tr>
                                    <th>Email:</th>
                                    <td>{contact.email}</td>
                                </tr>
                                <tr>
                                    <th>Phone:</th>
                                    <td>{formatPhone(contact.phone)}</td>
                                </tr>
                                <tr>
                                    <th>Address:</th>
                                    <td>
                                        {contact.addressLine1}
                                        {!contact.addressLine1 && <MissingItem field="Address Line 1"></MissingItem>}
                                        <br/>
                                        {contact.addressLine2 &&
                                            <>{contact.addressLine2}<br/></>
                                        }
                                        {contact.addressCity}
                                        {!contact.addressCity && <MissingItem field="Address City"></MissingItem>}
                                        , {contact.addressState} {contact.addressZip}
                                    </td>
                                </tr>
                                </tbody>
                            </table>


                            {tier2 &&
                            <IonGrid>
                                <IonRow>
                                    <IonCol>
                                        <IonSelect fill="outline" labelPlacement="floating" label="Status" disabled={openOpportunities} value={contact.status} onIonChange={(e) => updateContact('status', e.detail.value)}>

                                            <IonSelectOption value="NO_CONTACT">No Contact</IonSelectOption>
                                            <IonSelectOption value="DECEASED">Deceased</IonSelectOption>
                                            <IonSelectOption value="PREVIOUS">Previous</IonSelectOption>

                                        </IonSelect>
                                    </IonCol>
                                    <IonCol>
                                        {!openOpportunities &&
                                            <IonButton onClick={() => saveData()}>Update Status</IonButton>
                                        }
                                        {openOpportunities &&
                                            <>Close all open opportunities to edit customer status</>
                                        }
                                    </IonCol>
                                    <IonCol>

                                    </IonCol>
                                </IonRow>
                            </IonGrid>
                            }


                            <div>
                                <IonAccordionGroup>
                                    <IonAccordion value="first" toggleIcon={arrowUpOutline} toggleIconSlot="start">
                                        <IonItem slot="header" color="light">
                                            <IonLabel>Personal Information</IonLabel>
                                        </IonItem>
                                        <div className="ion-padding" slot="content">
                                            <IonGrid>
                                                <IonRow>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="First Name" labelPlacement="floating" value={contact.firstName} onIonChange={(e) => updateContact('firstName', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Middle Name" labelPlacement="floating" value={contact.middleName} onIonChange={(e) => updateContact('middleName', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Last Name" labelPlacement="floating" value={contact.lastName} onIonChange={(e) => updateContact('lastName', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonSelect fill="outline" label="Suffix" labelPlacement="floating" value={contact.suffix} onIonChange={(e) => updateContact('suffix', e?.detail.value)}>
                                                            <IonSelectOption value="Sr.">Sr.</IonSelectOption>
                                                            <IonSelectOption value="Jr.">Jr.</IonSelectOption>
                                                            <IonSelectOption value="I">I</IonSelectOption>
                                                            <IonSelectOption value="II">II</IonSelectOption>
                                                            <IonSelectOption value="III">III</IonSelectOption>
                                                        </IonSelect>
                                                    </IonCol>
                                                </IonRow>
                                                <IonRow>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Email" labelPlacement="floating" value={contact.email} onIonChange={(e) => updateContact('email', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Phone #" labelPlacement="floating" value={contact.phone} onIonChange={(e) => updateContact('phone', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Date Of Birth" labelPlacement="floating" type="date" value="**/**/****" onIonChange={(e) => updateContact('dob', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="SSN" labelPlacement="floating" value="**********" onIonChange={(e) => updateContact('ssn', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                </IonRow>
                                            </IonGrid>

                                            <p style={{textAlign: 'right'}}><IonButton onClick={() => saveData()}>Update
                                                Information</IonButton></p>

                                        </div>
                                    </IonAccordion>
                                    <IonAccordion value="second" toggleIcon={arrowUpOutline} toggleIconSlot="start">
                                        <IonItem slot="header" color="light">
                                            <IonLabel>Address Information</IonLabel>
                                        </IonItem>
                                        <div className="ion-padding" slot="content">
                                            <IonGrid>
                                                <IonRow>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Address Line 1" labelPlacement="floating" value={contact.addressLine1} onIonChange={(e) => updateContact('addressLine1', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                </IonRow>
                                                <IonRow>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Address Line 2" labelPlacement="floating" value={contact.addressLine2} onIonChange={(e) => updateContact('addressLine2', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                </IonRow>
                                                <IonRow>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="City" labelPlacement="floating" value={contact.addressCity} onIonChange={(e) => updateContact('addressCity', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonSelect fill="outline" label="State" labelPlacement="floating" value={contact.addressState} onIonChange={(e) => updateContact('addressState', e?.detail.value)}>
                                                            <IonSelectOption value="AL">AL</IonSelectOption>
                                                            <IonSelectOption value="AK">AK</IonSelectOption>
                                                            <IonSelectOption value="AZ">AZ</IonSelectOption>
                                                            <IonSelectOption value="AR">AR</IonSelectOption>
                                                            <IonSelectOption value="AS">AS</IonSelectOption>
                                                            <IonSelectOption value="CA">CA</IonSelectOption>
                                                            <IonSelectOption value="CO">CO</IonSelectOption>
                                                            <IonSelectOption value="CT">CT</IonSelectOption>
                                                            <IonSelectOption value="DE">DE</IonSelectOption>
                                                            <IonSelectOption value="DC">DC</IonSelectOption>
                                                            <IonSelectOption value="FL">FL</IonSelectOption>
                                                            <IonSelectOption value="GA">GA</IonSelectOption>
                                                            <IonSelectOption value="GU">GU</IonSelectOption>
                                                            <IonSelectOption value="HI">HI</IonSelectOption>
                                                            <IonSelectOption value="ID">ID</IonSelectOption>
                                                            <IonSelectOption value="IL">IL</IonSelectOption>
                                                            <IonSelectOption value="IN">IN</IonSelectOption>
                                                            <IonSelectOption value="IA">IA</IonSelectOption>
                                                            <IonSelectOption value="KS">KS</IonSelectOption>
                                                            <IonSelectOption value="KY">KY</IonSelectOption>
                                                            <IonSelectOption value="LA">LA</IonSelectOption>
                                                            <IonSelectOption value="ME">ME</IonSelectOption>
                                                            <IonSelectOption value="MD">MD</IonSelectOption>
                                                            <IonSelectOption value="MA">MA</IonSelectOption>
                                                            <IonSelectOption value="MI">MI</IonSelectOption>
                                                            <IonSelectOption value="MN">MN</IonSelectOption>
                                                            <IonSelectOption value="MS">MS</IonSelectOption>
                                                            <IonSelectOption value="MO">MO</IonSelectOption>
                                                            <IonSelectOption value="MT">MT</IonSelectOption>
                                                            <IonSelectOption value="NE">NE</IonSelectOption>
                                                            <IonSelectOption value="NV">NV</IonSelectOption>
                                                            <IonSelectOption value="NH">NH</IonSelectOption>
                                                            <IonSelectOption value="NJ">NJ</IonSelectOption>
                                                            <IonSelectOption value="NM">NM</IonSelectOption>
                                                            <IonSelectOption value="NY">NY</IonSelectOption>
                                                            <IonSelectOption value="NC">NC</IonSelectOption>
                                                            <IonSelectOption value="ND">ND</IonSelectOption>
                                                            <IonSelectOption value="MP">MP</IonSelectOption>
                                                            <IonSelectOption value="OH">OH</IonSelectOption>
                                                            <IonSelectOption value="OK">OK</IonSelectOption>
                                                            <IonSelectOption value="OR">OR</IonSelectOption>
                                                            <IonSelectOption value="PA">PA</IonSelectOption>
                                                            <IonSelectOption value="PR">PR</IonSelectOption>
                                                            <IonSelectOption value="RI">RI</IonSelectOption>
                                                            <IonSelectOption value="SC">SC</IonSelectOption>
                                                            <IonSelectOption value="SD">SD</IonSelectOption>
                                                            <IonSelectOption value="TN">TN</IonSelectOption>
                                                            <IonSelectOption value="TX">TX</IonSelectOption>
                                                            <IonSelectOption value="TT">TT</IonSelectOption>
                                                            <IonSelectOption value="UT">UT</IonSelectOption>
                                                            <IonSelectOption value="VT">VT</IonSelectOption>
                                                            <IonSelectOption value="VA">VA</IonSelectOption>
                                                            <IonSelectOption value="VI">VI</IonSelectOption>
                                                            <IonSelectOption value="WA">WA</IonSelectOption>
                                                            <IonSelectOption value="WV">WV</IonSelectOption>
                                                            <IonSelectOption value="WI">WI</IonSelectOption>
                                                            <IonSelectOption value="WY">WY</IonSelectOption>
                                                        </IonSelect>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonInput fill="outline" label="Zip" labelPlacement="floating" value={contact.addressZip} onIonChange={(e) => updateContact('addressZip', e?.detail.value)}></IonInput>
                                                    </IonCol>
                                                </IonRow>
                                            </IonGrid>

                                            <p style={{textAlign: 'right'}}><IonButton onClick={() => saveData()}>Update
                                                Information</IonButton>
                                            </p>
                                        </div>
                                    </IonAccordion>
                                    <IonAccordion value="third" toggleIcon={arrowUpOutline} toggleIconSlot="start">
                                        <IonItem slot="header" color="light">
                                            <IonLabel>Additional Information</IonLabel>
                                        </IonItem>
                                        <div className="ion-padding" slot="content">
                                            <IonGrid>
                                                <IonRow>
                                                    <IonCol>
                                                        <IonGrid>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonInput fill="outline" label="Annual Income" labelPlacement="floating" value={contact.annualIncome} onIonChange={(e) => updateContact('annualIncome', e?.detail.value)} type="number" min={0}></IonInput>
                                                                </IonCol>
                                                            </IonRow>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonSelect fill="outline" label="Employment Type" labelPlacement="floating" value={contact.employmentType} onIonChange={(e) => updateContact('employmentType', e?.detail.value)}>
                                                                        <IonSelectOption value="DISABLED">Disabled</IonSelectOption>
                                                                        <IonSelectOption value="SELF_EMPLOYED">Self
                                                                            Employed</IonSelectOption>
                                                                        <IonSelectOption value="UNEMPLOYED">Unemployed</IonSelectOption>
                                                                        <IonSelectOption value="RETIRED">Retired</IonSelectOption>
                                                                        <IonSelectOption value="EMPLOYED">Employed</IonSelectOption>
                                                                    </IonSelect>
                                                                </IonCol>
                                                            </IonRow>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonTextarea fill="outline" label="Employment Description" labelPlacement="floating" value={contact.employmentDescription} onIonChange={(e) => updateContact('employmentDescription', e?.detail.value)}></IonTextarea>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonGrid>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonGrid>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonInput fill="outline" label="Main Doctor" labelPlacement="floating" value={contact.mainDoctor} onIonChange={(e) => updateContact('mainDoctor', e?.detail.value)}></IonInput>
                                                                </IonCol>
                                                            </IonRow>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonTextarea fill="outline" label="Medications" labelPlacement="floating" value={contact.medications} onIonChange={(e) => updateContact('medications', e?.detail.value)}></IonTextarea>
                                                                </IonCol>
                                                            </IonRow>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonItem>
                                                                        <IonLabel>Smoker</IonLabel>
                                                                        <IonToggle checked={contact.smoker ? true : false} onIonChange={(e) => updateContact('smoker', e?.detail.value)}></IonToggle>
                                                                    </IonItem>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonGrid>
                                                    </IonCol>
                                                    <IonCol>
                                                        <IonGrid>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonInput value="" fill="outline" label="Fusion Customer ID" labelPlacement="floating"></IonInput>
                                                                </IonCol>
                                                            </IonRow>
                                                            <IonRow>
                                                                <IonCol>
                                                                    <IonSelect fill="outline" label="ETC" labelPlacement="floating">
                                                                        <IonSelectOption value="ASSIST">Assist</IonSelectOption>
                                                                        <IonSelectOption value="ENTOUCH">Entouch</IonSelectOption>
                                                                    </IonSelect>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonGrid>
                                                    </IonCol>
                                                </IonRow>
                                            </IonGrid>


                                            <p style={{textAlign: 'right'}}><IonButton onClick={() => saveData()}>Update
                                                Information</IonButton>
                                            </p>


                                        </div>
                                    </IonAccordion>
                                    <IonAccordion value="fourth" toggleIcon={arrowUpOutline} toggleIconSlot="start">
                                        <IonItem slot="header" color="light">
                                            <IonLabel>Dependent Information</IonLabel>
                                        </IonItem>
                                        <div className="ion-padding" slot="content">
                                            {dependents && dependents.map((dep, index) => {
                                                if (editDependent && editDependent.id === dep.id) {
                                                    return (
                                                        <IonCard style={{float:'left', maxWidth: '400px'}} key={'dep_'+index}>
                                                            <IonCardHeader>
                                                                <IonCardTitle>
                                                                    Dependent #{index + 1}
                                                                    <div style={{float: 'right', display: 'inline-block'}}>
                                                                        <IonButton size="small" fill="clear" onClick={() => editDependent.id === dep.id ? setEditDependent(undefined) : setEditDependent(dep)}><IonIcon size="small" md={pencilOutline} ios={pencilSharp}></IonIcon></IonButton>
                                                                        <IonButton size="small" fill="clear" onClick={() => alert('not implemented yet')}><IonIcon size="small" md={trashOutline} ios={trashSharp}></IonIcon></IonButton>
                                                                    </div>
                                                                </IonCardTitle>
                                                            </IonCardHeader>
                                                            <IonCardContent>
                                                                <IonInput fill="outline" label="First Name" labelPlacement="floating" value={editDependent.firstName} onIonChange={(e) => updateDependent('firstName', e.detail.value)}></IonInput>
                                                                <IonInput fill="outline" label="Middle Name" labelPlacement="floating" value={editDependent.middleName} onIonChange={(e) => updateDependent('middleName', e.detail.value)}></IonInput>

                                                                <IonInput fill="outline" label="Last Name" labelPlacement="floating" value={editDependent.lastName} onIonChange={(e) => updateDependent('lastName', e.detail.value)}></IonInput>
                                                                <IonInput fill="outline" label="Suffix" labelPlacement="floating" value={editDependent.suffix} onIonChange={(e) => updateDependent('suffix', e.detail.value)}></IonInput>
                                                                <IonInput fill="outline" label="DOB" labelPlacement="floating" value={editDependent.dob} onIonChange={(e) => updateDependent('dob', e.detail.value)} type="date"></IonInput>

                                                                <IonInput fill="outline" label="SSN" labelPlacement="floating" value={editDependent.ssn} onIonChange={(e) => updateDependent('ssn', e.detail.value)}></IonInput>

                                                                <div style={{textAlign: 'right'}}>
                                                                    <IonButton onClick={() => saveEditDependent()}>Save</IonButton>
                                                                </div>
                                                            </IonCardContent>
                                                        </IonCard>
                                                    )
                                                }

                                                return (
                                                    <IonCard style={{float:'left', maxWidth: '400px'}} key={'dep_'+index}>
                                                        <IonCardHeader>
                                                            <IonCardTitle>
                                                                Dependent #{index + 1}
                                                                <div style={{float: 'right', display: 'inline-block'}}>
                                                                    <IonButton size="small" fill="clear" onClick={() => setEditDependent(dep)}><IonIcon size="small" md={pencilOutline} ios={pencilSharp}></IonIcon></IonButton>
                                                                    <IonButton size="small" fill="clear" onClick={() => deleteDependent(dep.id)}><IonIcon size="small" md={trashOutline} ios={trashSharp}></IonIcon></IonButton>
                                                                </div>
                                                            </IonCardTitle>
                                                        </IonCardHeader>
                                                        <IonCardContent>
                                                            <b>Name:</b> {dep.firstName} {dep.middleName} {dep.lastName} {dep.suffix}<br />
                                                            <b>Dob:</b> {dep.dob}<br />
                                                            <b>SSN:</b> {dep.ssn}
                                                        </IonCardContent>
                                                    </IonCard>
                                                );


                                            })}
                                            {addDependent &&


                                                <IonCard style={{float:'left', maxWidth: '400px'}}>
                                                    <IonCardHeader>
                                                        <IonCardTitle>
                                                            Dependent #{dependents ? dependents.length + 1 : 1}
                                                            <div style={{float: 'right', display: 'inline-block'}}>

                                                                <IonButton size="small" fill="clear" onClick={() => setAddDependent(undefined)}><IonIcon size="small" md={trashOutline} ios={trashSharp}></IonIcon></IonButton>
                                                            </div>
                                                        </IonCardTitle>
                                                    </IonCardHeader>
                                                    <IonCardContent>
                                                        <IonInput fill="outline" label="First Name" labelPlacement="floating" value={addDependent.firstName} onIonChange={(e) => updateAddDependent('firstName', e.detail.value)}></IonInput>
                                                        <IonInput fill="outline" label="Middle Name" labelPlacement="floating" value={addDependent.middleName} onIonChange={(e) => updateAddDependent('middleName', e.detail.value)}></IonInput>
                                                        <IonInput fill="outline" label="Last Name" labelPlacement="floating" value={addDependent.lastName} onIonChange={(e) => updateAddDependent('lastName', e.detail.value)}></IonInput>
                                                        <IonInput fill="outline" label="Suffix" labelPlacement="floating" value={addDependent.suffix} onIonChange={(e) => updateAddDependent('suffix', e.detail.value)}></IonInput>
                                                        <IonInput fill="outline" label="DOB" labelPlacement="floating" value={addDependent.dob} onIonChange={(e) => updateAddDependent('dob', e.detail.value)} type="date"></IonInput>
                                                        <IonInput fill="outline" label="SSN" labelPlacement="floating" value={addDependent.ssn} onIonChange={(e) => updateAddDependent('ssn', e.detail.value)}></IonInput>

                                                        <div style={{textAlign: 'right'}}>
                                                            <IonButton onClick={() => saveAddDependent()}>Save</IonButton>
                                                        </div>
                                                    </IonCardContent>
                                                </IonCard>

                                            }
                                            {!addDependent && dependents?.length && dependents.length < 7 &&

                                                <IonButton style={{float: 'left'}} onClick={() => setAddDependent({firstName: '', lastName: '', middleName: '', suffix: '', dob: '', ssn: '', contactId: contact ? contact.contactId : null, id: null, createdDatetime: new Date()})}><IonIcon md={addOutline} ios={addSharp}></IonIcon>&nbsp;&nbsp;Add Dependent</IonButton>
                                            }




                                        </div>
                                    </IonAccordion>
                                    <IonAccordion value="fifth" toggleIcon={arrowUpOutline} toggleIconSlot="start">
                                        <IonItem slot="header" color="light">
                                            <IonLabel>Attachments</IonLabel>
                                        </IonItem>
                                        <div className="ion-padding" slot="content">
                                            <b>Photo 1:</b>
                                            {contact.photo1Id &&
                                                <><IonButton onClick={() => expandImage(contact.photo1Id)} fill="outline" title="View image">
                                                    <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Photo 1
                                                </IonButton>
                                                    <IonButton onClick={() => setChangingPhoto1(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Change</IonButton>
                                                    <IonButton onClick={() => promptImageReplace(1)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {!contact.photo1Id &&
                                                <>
                                                    <IonButton onClick={() => setChangingPhoto1(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Add Photo</IonButton>
                                                    <IonButton onClick={() => promptImageReplace(1)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {changingPhoto1 &&
                                                <>
                                                    <IonItem key="upload"><AcaUploadFile callback={addPhoto1Attachment} acceptedFileTypes={['.png', '.jpg', '.jpeg']} /></IonItem>
                                                </>
                                            }
                                            <br/>
                                            <b>Photo 2:</b>
                                            {contact.photo2Id &&
                                                <><IonButton onClick={() => expandImage(contact.photo2Id)} fill="outline" title="View image">
                                                    <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Photo 2
                                                </IonButton>
                                                    <IonButton onClick={() => setChangingPhoto2(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Change</IonButton>
                                                    <IonButton onClick={() => promptImageReplace(2)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {!contact.photo2Id &&
                                                <>
                                                    <IonButton onClick={() => setChangingPhoto2(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Add Photo</IonButton>
                                                    <IonButton onClick={() => promptImageReplace(2)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {changingPhoto2 &&
                                                <>
                                                    <IonItem key="upload"><AcaUploadFile callback={addPhoto2Attachment} acceptedFileTypes={['.png', '.jpg', '.jpeg']} /></IonItem>
                                                </>
                                            }

                                        </div>
                                    </IonAccordion>
                                </IonAccordionGroup>


                            </div>


                            <div style={{float: 'right', display: 'inline-block'}}>

                            </div>
                            <div style={{clear: 'both'}}></div>
                        </div>
                    }
                    <div style={{height: '60px'}}></div>
                </div>
            </IonContent>
        </IonPage>
    )
}

export default AcaEditContact;